/**
 * Splits a translated string containing `<tag>…</tag>` markers into ordered
 * segments so each platform can render the tagged spans its own way (web:
 * router links, mobile: pressable Text). Keeps legal sentences like the
 * ToS/Privacy agreement as ONE translation key per locale — word order and
 * grammar stay correct in every language instead of being concatenated from
 * fragments.
 *
 * Example:
 *   parseTaggedText('Agree to the <terms>Terms</terms> and <privacy>Privacy</privacy>.')
 *   → [
 *       { tag: null, text: 'Agree to the ' },
 *       { tag: 'terms', text: 'Terms' },
 *       { tag: null, text: ' and ' },
 *       { tag: 'privacy', text: 'Privacy' },
 *       { tag: null, text: '.' },
 *     ]
 *
 * Unknown/unclosed tags are left in place as plain text rather than dropped,
 * so a mistranslated locale string degrades to visible text, never lost words.
 */
export interface TaggedTextSegment {
  /** Tag name for tagged spans, null for plain text between tags. */
  tag: string | null;
  text: string;
}

const TAG_PATTERN = /<(\w+)>(.*?)<\/\1>/g;

export function parseTaggedText(input: string): TaggedTextSegment[] {
  const segments: TaggedTextSegment[] = [];
  let lastIndex = 0;

  for (const match of input.matchAll(TAG_PATTERN)) {
    if (match.index > lastIndex) {
      segments.push({ tag: null, text: input.slice(lastIndex, match.index) });
    }
    segments.push({ tag: match[1], text: match[2] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < input.length) {
    segments.push({ tag: null, text: input.slice(lastIndex) });
  }

  return segments;
}
